// dd-deepdive.jsx — Assessment Deep Dive (section 02), V2 layout: a grouped left
// sidebar of assessments + per-assessment blocks. Ported from the Claude Design
// source `dd-app.jsx`; `window.*` globals are replaced with ES imports, and the DD
// data model is received as a `data` prop so it can be merged with live backend
// numbers upstream (see pages/ExecutiveSummary).
import React, { useEffect, useState } from "react";
import "./deepdive.css";

import { ddSystemFor, ddBandColor, ddScoreColor } from "./dd-data";
import { BandRibbon, StackedDist, DimBars, TypeDonut } from "./dd-charts";
import {
  WellnessWeighted, VbiPatterns, ProqolProfile,
  SafetyCallout, OrgKeyDimensions, CrossAssessment,
} from "./dd-modules";

const DD_STORE = "meridian-deepdive-v1";

// Sidebar navigation groups (V2). Only the 10 shown assessments; Moral Injury /
// Capability / Follow-Up and the program journey are not surfaced in V2.
const DD_NAV_GROUPS = [
  { label: "Behavioral", ids: ["vasana", "wellness", "vbi"] },
  { label: "Clinical", ids: ["isi", "gad", "phq", "pcl", "mspss", "audit", "proqol"] },
];

// Per-assessment chart removals (design request):
//  · Dimension breakdown hidden for these assessments…
const HIDE_DIMS = new Set(["isi", "gad", "phq", "pcl", "audit"]);
//  · bespoke module hidden: Readiness "Weighted contribution", Quality of Life
//    "Three-subscale profile", and the "Clinical safety pathway" on Alcohol,
//    Happiness (phq) and Stress (pcl).
const HIDE_MODULE = new Set(["wellness", "audit", "proqol", "phq", "pcl"]);

function Bespoke({ a }) {
  switch (a.module) {
    case "wellness": return <WellnessWeighted weights={a.weights} />;
    case "vbi": return <VbiPatterns pressure={a.pressure} protection={a.protection} />;
    case "proqol": return <ProqolProfile subscales={a.subscales} />;
    case "safety": return <SafetyCallout safety={a.safety} name={a.name.replace(/\s*\(.*\)/, "")} />;
    default: return null;
  }
}

function ContextHeader({ a }) {
  return (
    <div className="dd-context">
      <div className="dd-context__main">
        <h3 className="dd-context__name">{a.name}</h3>
        <p className="dd-context__measures">{a.measures}</p>
      </div>
      <div className="dd-context__meta">
        <div className="dd-context__stat"><b>{a.n}</b><span>Respondents (n)</span></div>
        <div className="dd-context__stat"><b>{a.part}%</b><span>Participation</span></div>
      </div>
    </div>
  );
}

function HeadlineResult({ a, system }) {
  const bandIdx = a.bands.findIndex((b) => b.label === a.band);
  const bandObj = bandIdx >= 0 ? a.bands[bandIdx] : null;
  const color = (bandObj && bandObj.color) || ddBandColor(bandIdx < 0 ? 0 : bandIdx, a.bands.length, a.higherWorse, system);
  return (
    <div className="dd-headline">
      <div className="dd-headline__left">
        <div className="dd-headline__score" style={{ color }}>{a.score}<small>/100</small></div>
        <div className="dd-headline__meta">
          <span className="dd-headline__band" style={{ color }}>{a.band}</span>
          {a.trend && a.trend.note ? <span className="dd-headline__note">{a.trend.note}</span> : null}
        </div>
      </div>
      <div className="dd-headline__ribbon">
        <span className="dd-block__cap">Where the group lands</span>
        <BandRibbon bands={a.bands} score={a.score} band={a.band} higherWorse={a.higherWorse} system={system} />
      </div>
    </div>
  );
}

function Block({ cap, sub, children }) {
  return (
    <section className="dd-block">
      <div className="dd-block__head">
        <h4 className="dd-block__title">{cap}</h4>
        {sub ? <p className="dd-block__sub">{sub}</p> : null}
      </div>
      {children}
    </section>
  );
}

function ScoredTab({ a }) {
  const system = ddSystemFor(a.id);
  const dimNote = a.id === "vbi"
    ? "Burnout dimensions are shown health-aligned (higher = healthier), so the recovery and exhaustion floors read as low bars."
    : null;
  return (
    <div className="dd-tabbody">
      <ContextHeader a={a} />
      <section className="dd-block">
        <HeadlineResult a={a} system={system} />
      </section>
      <Block cap="Population band distribution" sub="The share of people in each result band — the spread an average can’t show.">
        <StackedDist
          dist={a.dist}
          bands={a.distBands || a.bands}
          higherWorse={a.distHigherWorse != null ? a.distHigherWorse : a.higherWorse}
          system={system}
        />
      </Block>
      {!HIDE_DIMS.has(a.id) && a.dims && a.dims.length >= 2 ? (
        <Block cap="Dimension breakdown" sub="Each subscale on a 0–100 health-aligned scale, sorted weakest to strongest.">
          <DimBars dims={a.dims} note={dimNote} system={system} />
        </Block>
      ) : null}
      {a.module && !HIDE_MODULE.has(a.id) ? <Bespoke a={a} /> : null}
    </div>
  );
}

function VasanaTab({ a, typeIndex }) {
  return (
    <div className="dd-tabbody">
      <ContextHeader a={a} />
      <Block cap="Vasana Type Composition">
        <TypeDonut mix={a.mix} />
      </Block>
      <Block cap="Organizational Key Dimensions" sub="A behavioral read of how your organization collectively moves, trusts, processes, and responds under pressure.">
        <OrgKeyDimensions mix={a.mix} />
      </Block>
      <CrossAssessment typeIndex={typeIndex} />
    </div>
  );
}

export function DeepDive({ data }) {
  const DD = data;
  const [tab, setTab] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem(DD_STORE) || "{}"); if (s.tab && DD.data[s.tab]) return s.tab; } catch (e) {}
    return "vasana";
  });

  useEffect(() => {
    try { localStorage.setItem(DD_STORE, JSON.stringify({ tab })); } catch (e) {}
  }, [tab]);

  const a = DD.data[tab] || DD.data.vasana;

  return (
    <section className="dd-section" id="deep-dive" data-screen-label="Assessment Deep Dive">
      <div className="dd-divider">
        <div className="dd-wrap dd-divider__inner">
          <h2 className="dd-section__title">Assessment Deep Dive</h2>
          <p className="dd-section__descr">A focused, group-level read on each assessment.</p>
        </div>
      </div>

      <div className="dd-wrap dd-layout">
        <aside className="dd-side" role="tablist" aria-label="Assessments">
          <div className="dd-side__header">
            <i className="ph ph-list-magnifying-glass"></i>
            <span>Browse assessments</span>
          </div>
          {DD_NAV_GROUPS.map((g) => (
            <div className="dd-side__group" key={g.label}>
              <div className="dd-side__grouplabel">{g.label}</div>
              {g.ids.map((id) => {
                const d = DD.data[id];
                if (!d) return null;
                const dot = d.categorical
                  ? "var(--vas-primary)"
                  : d.score == null
                    ? "rgba(255,255,255,0.25)"
                    : ddScoreColor(d.higherWorse ? 100 - d.score : d.score, ddSystemFor(id));
                return (
                  <button key={id} data-tab={id} role="tab" aria-selected={a.id === id}
                    className={"dd-navitem" + (a.id === id ? " is-active" : "")}
                    onClick={() => setTab(id)}>
                    <span className="dd-navitem__dot" style={{ background: dot }}></span>
                    <span className="dd-navitem__label">{d.tab}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </aside>

        <div className="dd-main">
          {a.categorical ? <VasanaTab a={a} typeIndex={DD.typeIndex} /> : <ScoredTab a={a} />}
        </div>
      </div>
    </section>
  );
}
